import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

export function sizeDisplayLabel(value, mapping = {}) {
  const raw = String(value ?? '').trim();
  if (typeof mapping[raw] === 'string' && mapping[raw].trim()) return mapping[raw].trim();
  return /^\d+(?:[.,]\d+)?(?:\s*[-–]\s*\d+(?:[.,]\d+)?)?$/.test(raw) ? `Size ${raw}` : raw;
}

export function pairTotal(firstPrice, secondPrice) {
  return Math.max(firstPrice, secondPrice);
}

export function buildPairItems(first, second, bundleId, sizeLabels = {}) {
  if (!first?.available || !second?.available) throw new Error('Please choose two available pairs.');
  const item = (variant, quantity) => {
    const properties = { _barevon_offer: 'buy-one-get-one-free', _barevon_bundle: bundleId };
    const size = sizeLabels[variant.id];
    if (size) properties[size.startsWith('US ') ? 'US size' : 'Size'] = size;
    return { id: Number(variant.id), quantity, properties };
  };
  if (String(first.id) === String(second.id)) return [item(first, 2)];
  return [item(first, 1), item(second, 1)];
}

export function eligibleQuantity(cart, productId) {
  const ids = new Set((Array.isArray(productId) ? productId : [productId]).map((value) => String(value).trim()));
  return (cart?.items || []).reduce((total, item) => total + (ids.has(String(item.product_id)) ? Number(item.quantity) : 0), 0);
}

export function inspectBundle(cart, bundleId, expectedPrice, discountTitle) {
  const items = (cart?.items || []).filter((item) => item.properties?._barevon_bundle === bundleId);
  const quantity = items.reduce((total, item) => total + Number(item.quantity), 0);
  const total = items.reduce((sum, item) => sum + Number(item.final_line_price), 0);
  const titles = items.flatMap((item) => [
    ...(item.discounts || []).map((discount) => discount.title),
    ...(item.line_level_discount_allocations || []).map((discount) => discount.discount_application?.title),
    ...(item.discount_allocations || []).map((discount) => discount.discount_application?.title),
  ]);
  const discountApplied = !discountTitle || titles.includes(discountTitle);
  return { items, quantity, total, valid: quantity === 2 && Number.isFinite(total) && total === expectedPrice && discountApplied };
}

export class BarevonStudioOffer extends HTMLElement {
  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;
    this.controller = new AbortController();
    this.buttons = new Map();
    this.generation = 0;
    this.busy = false;
    this.singleBusy = false;
    this.nativeReady = false;
    this.mode = 'single';
    this.colorMenus = new Map();
    this.selectionPending = false;
    this.cartHasEligible = false;
    this.uncertain = false;
    this.section = this.closest('.shopify-section');
    try {
      this.config = JSON.parse(this.querySelector('[data-bogo-config]').textContent);
      this.variants = this.config.variants;
      this.eligibleProductIds = [...new Set([this.config.productId, ...(Array.isArray(this.config.eligibleProductIds) ? this.config.eligibleProductIds : [])].map((value) => String(value).trim()).filter(Boolean))];
      if (!Array.isArray(this.variants) || !this.variants.length || !this.section) throw new Error('The pair offer could not load. Please refresh this page.');
      const names = this.config.options.map((value) => String(value).toLowerCase());
      this.colorIndex = names.findIndex((name) => [String(this.config.colorOptionName).toLowerCase(), 'color', 'colour'].includes(name));
      this.sizeIndex = names.findIndex((name) => name === String(this.config.sizeOptionName).toLowerCase());
      if (this.colorIndex < 0 || this.sizeIndex < 0) throw new Error('The pair options could not load. Please contact us for help.');
      try { this.sizeMap = JSON.parse(this.config.sizeDisplayMap || '{}'); } catch { this.sizeMap = {}; }
      if (!this.sizeMap || typeof this.sizeMap !== 'object' || Array.isArray(this.sizeMap)) this.sizeMap = {};
      this.sizeMap = { ...this.config.sizeLabels, ...this.sizeMap };
      this.firstVariantId = String(this.formComponent()?.querySelector('input[name="id"]')?.value || this.config.selectedVariantId || '');
      this.firstColorSelect = this.querySelector('[data-bogo-first-color]');
      this.firstSizeSelect = this.querySelector('[data-bogo-first-size]');
      this.colorSelect = this.querySelector('[data-bogo-color]');
      this.sizeSelect = this.querySelector('[data-bogo-size]');
      this.populateColors();
      this.populateColors(this.firstColorSelect);
      this.syncFirstSelectors();
      const initial = this.variants.find((variant) => variant.available && String(variant.id) === this.firstVariantId) || this.variants.find((variant) => variant.available);
      if (initial) this.colorSelect.value = initial.options[this.colorIndex];
      this.populateSizes(initial?.options[this.sizeIndex]);
      const { signal } = this.controller;
      this.firstColorSelect.addEventListener('change', () => { this.clearError(); this.populateSizes(this.firstSizeSelect.value, this.firstColorSelect, this.firstSizeSelect); this.selectFirstVariant(); }, { signal });
      this.firstSizeSelect.addEventListener('change', () => { this.clearError(); this.selectFirstVariant(); }, { signal });
      this.colorSelect.addEventListener('change', () => { this.clearError(); this.populateSizes(this.sizeSelect.value); this.refresh(); this.previewPairMedia('second'); }, { signal });
      this.sizeSelect.addEventListener('change', () => { this.clearError(); this.refresh(); this.previewPairMedia('second'); }, { signal });
      this.querySelector('[data-bogo-fit]').addEventListener('click', () => this.openFitGuide(), { signal });
      this.querySelectorAll('[data-bogo-mode]').forEach((radio) => radio.addEventListener('change', () => this.setMode(radio.value), { signal }));
      this.installColorMenu(this.firstColorSelect);
      this.installColorMenu(this.colorSelect);
      document.addEventListener('click', (event) => { for (const menu of this.colorMenus.values()) if (!menu.root.contains(event.target)) this.closeColorMenu(menu); }, { signal });
      document.addEventListener('focusin', (event) => { for (const menu of this.colorMenus.values()) if (!menu.root.contains(event.target)) this.closeColorMenu(menu); }, { signal });
      window.addEventListener('click', (event) => this.captureClick(event), { capture: true, signal });
      window.addEventListener('submit', (event) => this.captureSubmit(event), { capture: true, signal });
      this.section.addEventListener(StandardEvents.productSelect, (event) => this.onProductSelect(event), { signal });
      document.addEventListener(StandardEvents.cartLinesUpdate, (event) => this.onCartUpdate(event), { signal });
      this.observer = new MutationObserver(() => this.syncControls());
      this.observer.observe(this.section, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'value'] });
      this.setAttribute('data-ready', '');
      this.setStatus('');
      this.setMode('single');
      this.refresh();
      customElements.whenDefined('variant-picker').then(() => {
        if (!this.isConnected || !this._mounted) return;
        if (typeof this.nativePicker()?.updateSelectedOption !== 'function') throw new Error('The product options could not load. Please refresh the page.');
        this.nativeReady = true;
        this.setAttribute('data-native-ready', '');
        this.refresh();
      }).catch((error) => this.showError(error.message));
    } catch (error) {
      this.showError(error.message || 'The pair offer could not load. Please refresh this page.');
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.removeAttribute('data-native-ready');
    if (!this.busy && !this.singleBusy) this.syncNativePickerLock(false);
    for (const [button, original] of this.buttons || []) {
      button.removeAttribute('data-bv-studio-offer-button');
      button.removeAttribute('aria-busy');
      button.querySelector('.bv-studio-offer-cta-label')?.remove();
      if (original.label === null) button.removeAttribute('aria-label'); else button.setAttribute('aria-label', original.label);
      button.disabled = !this.firstVariant()?.available;
    }
    this._mounted = false;
  }

  formComponent() {
    return this.section?.querySelector(`product-form-component[data-product-id="${this.config.productId}"]`);
  }

  nativePicker() {
    return this.section?.querySelector(`variant-picker[data-product-id="${this.config.productId}"]`);
  }

  firstVariant() {
    return this.variants?.find((variant) => String(variant.id) === this.firstVariantId);
  }

  secondVariant() {
    const first = this.firstVariant();
    return this.variants?.find((variant) => variant.options[this.colorIndex] === this.colorSelect.value && variant.options[this.sizeIndex] === this.sizeSelect.value && variant.options.every((value, index) => index === this.colorIndex || index === this.sizeIndex || value === first?.options[index]));
  }

  populateColors(select = this.colorSelect) {
    select.replaceChildren(...[...new Set(this.variants.map((variant) => variant.options[this.colorIndex]))].map((color) => {
      const option = new Option(color, color);
      option.disabled = !this.variants.some((variant) => variant.options[this.colorIndex] === color && variant.available);
      return option;
    }));
  }

  populateSizes(preferred, colorSelect = this.colorSelect, sizeSelect = this.sizeSelect, retainUnavailable = false) {
    const variants = this.variants.filter((variant) => variant.options[this.colorIndex] === colorSelect.value);
    const sizes = [...new Set(variants.map((variant) => variant.options[this.sizeIndex]))];
    sizeSelect.replaceChildren(...sizes.map((size) => {
      const available = variants.some((variant) => variant.options[this.sizeIndex] === size && variant.available);
      const option = new Option(`${sizeDisplayLabel(size, this.sizeMap).replace(/^US\s+/, '')}${available ? '' : ' (sold out)'}`, size);
      option.disabled = !available;
      return option;
    }));
    const preferredOption = [...sizeSelect.options].find((option) => option.value === String(preferred) && (retainUnavailable || !option.disabled));
    sizeSelect.value = preferredOption?.value || [...sizeSelect.options].find((option) => !option.disabled)?.value || '';
  }

  setMode(mode) {
    if (!['single', 'bogo'].includes(mode)) return;
    if (this.busy || this.singleBusy || this.selectionPending || this.uncertain) {
      this.querySelectorAll('[data-bogo-mode]').forEach((radio) => { radio.checked = radio.value === this.mode; });
      return;
    }
    this.mode = mode;
    this.syncFirstSelectors();
    this.dataset.mode = mode;
    this.querySelectorAll('[data-bogo-mode]').forEach((radio) => { radio.checked = radio.value === mode; });
    this.querySelectorAll('[data-bogo-card]').forEach((card) => card.toggleAttribute('data-selected', card.dataset.bogoCard === mode));
    this.querySelector('[data-bogo-pairs]').hidden = mode !== 'bogo';
    this.querySelector('[data-bogo-second-pair]').hidden = mode !== 'bogo';
    this.querySelector('[data-bogo-saving-line]').hidden = mode !== 'bogo';
    this.querySelector('[data-bogo-terms]').hidden = mode !== 'bogo';
    for (const menu of this.colorMenus.values()) this.closeColorMenu(menu);
    this.clearError();
    this.setStatus('');
    this.refresh();
    this.previewPairMedia(mode === 'single' ? 'first' : 'second');
  }

  syncFirstSelectors() {
    const first = this.firstVariant();
    if (!first || !this.firstColorSelect || !this.firstSizeSelect) return;
    this.firstColorSelect.value = first.options[this.colorIndex];
    this.populateSizes(first.options[this.sizeIndex], this.firstColorSelect, this.firstSizeSelect, true);
  }

  selectFirstVariant() {
    if (this.mode !== 'bogo' || this.busy || this.singleBusy || this.selectionPending || this.uncertain || !this.nativeReady) return;
    const previous = this.firstVariant();
    const desired = this.variants.find((variant) => variant.options[this.colorIndex] === this.firstColorSelect.value && variant.options[this.sizeIndex] === this.firstSizeSelect.value && variant.options.every((value, index) => index === this.colorIndex || index === this.sizeIndex || value === previous?.options[index]));
    if (!desired?.available) { this.showError('Please choose an available first pair.'); return; }
    if (String(desired.id) === this.firstVariantId) { this.previewPairMedia('first'); this.refresh(); return; }
    const picker = this.nativePicker();
    try {
      const controls = desired.options.map((value, index) => [...picker.querySelectorAll('input[data-option-value-id]')].find((input) => input.dataset.optionName === this.config.options[index] && input.value === value));
      if (controls.some((input) => !input)) throw new Error('Please refresh the page to update your first pair.');
      for (const control of controls) picker.updateSelectedOption(control);
      const changed = controls[this.colorIndex];
      const originalVariantId = changed.dataset.variantId;
      changed.dataset.variantId = String(desired.id);
      try { changed.dispatchEvent(new Event('change', { bubbles: true })); }
      finally { if (originalVariantId === undefined) delete changed.dataset.variantId; else changed.dataset.variantId = originalVariantId; }
    } catch (error) {
      this.firstVariantId = '';
      this.showError(error.message || 'Your first pair could not be updated. Please refresh the page.');
      this.syncControls();
    }
  }

  previewPairMedia(pair) {
    const variant = pair === 'first' ? this.firstVariant() : this.secondVariant();
    if (!variant) return;
    this.dispatchEvent(new CustomEvent('barevon:pair-media-preview', {
      bubbles: true,
      detail: {
        productId: String(this.config.productId), pair,
        color: variant.options[this.colorIndex], variantId: String(variant.id),
        mediaId: variant.featured_media?.id || variant.featured_image?.id || null,
        variant,
      },
    }));
  }

  syncNativePickerLock(locked) {
    if (locked && !this.nativePickerLock) {
      const element = this.nativePicker();
      if (!element) return;
      this.nativePickerLock = { element, inert: element.inert, busy: element.getAttribute('aria-busy') };
      element.inert = true;
      element.setAttribute('aria-busy', 'true');
    } else if (!locked && this.nativePickerLock) {
      const { element, inert, busy } = this.nativePickerLock;
      element.inert = inert;
      if (busy === null) element.removeAttribute('aria-busy'); else element.setAttribute('aria-busy', busy);
      this.nativePickerLock = null;
    }
  }

  installColorMenu(select) {
    const root = select.closest('[data-bogo-color-container]');
    root.classList.add('bv-studio-offer__color-picker');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bv-studio-offer__color-button';
    button.id = `${select.id}-combobox`;
    button.setAttribute('role', 'combobox');
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    const image = document.createElement('img');
    image.className = 'bv-studio-offer__color-image'; image.alt = ''; image.width = 38; image.height = 38;
    const label = document.createElement('span');
    button.append(image, label);
    const list = document.createElement('div');
    list.className = 'bv-studio-offer__color-list'; list.id = `${select.id}-options`; list.hidden = true;
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', root.querySelector('label').textContent);
    button.setAttribute('aria-controls', list.id);
    root.querySelector('label').htmlFor = button.id;
    root.append(button, list);
    select.hidden = true;
    const menu = { root, select, button, list, image, label, options: [], index: 0 };
    this.colorMenus.set(select, menu);
    const { signal } = this.controller;
    [...select.options].forEach((option, index) => {
      const item = document.createElement('button');
      item.type = 'button'; item.className = 'bv-studio-offer__color-option'; item.id = `${list.id}-${index}`;
      item.setAttribute('role', 'option'); item.tabIndex = -1;
      const source = this.config.colorImages?.[option.value];
      if (source) { const thumbnail = document.createElement('img'); thumbnail.src = source; thumbnail.alt = ''; thumbnail.width = 45; thumbnail.height = 45; item.append(thumbnail); }
      const text = document.createElement('span'); text.textContent = option.textContent; item.append(text);
      item.disabled = option.disabled;
      item.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
      item.addEventListener('click', () => this.chooseColor(menu, index), { signal });
      item.addEventListener('pointerenter', () => this.highlightColor(menu, index), { signal });
      list.append(item); menu.options.push(item);
    });
    button.addEventListener('click', () => list.hidden ? this.openColorMenu(menu) : this.closeColorMenu(menu), { signal });
    button.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); this.closeColorMenu(menu); return; }
      if (event.key === 'Tab') { this.closeColorMenu(menu); return; }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const wasClosed = list.hidden;
        if (wasClosed) this.openColorMenu(menu);
        const enabled = menu.options.map((item, index) => item.disabled ? -1 : index).filter((index) => index >= 0);
        const current = enabled.indexOf(menu.index);
        const index = event.key === 'Home' ? enabled[0] : event.key === 'End' ? enabled.at(-1) : wasClosed ? menu.index : enabled[(current + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length];
        this.highlightColor(menu, index);
      } else if ((event.key === 'Enter' || event.key === ' ') && !list.hidden) {
        event.preventDefault(); this.chooseColor(menu, menu.index);
      }
    }, { signal });
  }

  openColorMenu(menu) {
    if (menu.button.disabled) return;
    for (const other of this.colorMenus.values()) if (other !== menu) this.closeColorMenu(other);
    menu.list.hidden = false;
    const rect = menu.button.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - 8;
    const above = rect.top - 8;
    const openAbove = below < Math.min(menu.list.scrollHeight, 290) && above > below;
    menu.list.dataset.side = openAbove ? 'top' : 'bottom';
    menu.list.style.maxHeight = `${Math.max(60, Math.min(290, openAbove ? above : below))}px`;
    menu.button.setAttribute('aria-expanded', 'true');
    this.highlightColor(menu, Math.max(0, menu.select.selectedIndex));
  }

  closeColorMenu(menu) {
    menu.list.hidden = true;
    menu.button.setAttribute('aria-expanded', 'false');
    menu.button.removeAttribute('aria-activedescendant');
  }

  highlightColor(menu, index) {
    if (!menu.options[index] || menu.options[index].disabled) return;
    menu.index = index;
    menu.options.forEach((option, i) => option.toggleAttribute('data-highlighted', i === index));
    menu.button.setAttribute('aria-activedescendant', menu.options[index].id);
    if (!menu.list.hidden) menu.options[index].scrollIntoView({ block: 'nearest' });
  }

  chooseColor(menu, index) {
    if (menu.button.disabled || !menu.options[index] || menu.options[index].disabled) return;
    menu.select.value = menu.select.options[index].value;
    this.closeColorMenu(menu);
    menu.button.focus({ preventScroll: true });
    menu.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  syncColorMenus() {
    for (const menu of this.colorMenus.values()) {
      const text = menu.select.selectedOptions[0]?.textContent || 'Choose colour';
      if (menu.label.textContent !== text) menu.label.textContent = text;
      const source = this.config.colorImages?.[menu.select.value];
      menu.image.hidden = !source;
      if (source && menu.image.getAttribute('src') !== source) menu.image.src = source;
      if (menu.button.disabled !== menu.select.disabled) menu.button.disabled = menu.select.disabled;
      menu.options.forEach((option, index) => option.setAttribute('aria-selected', String(index === menu.select.selectedIndex)));
      if (menu.button.disabled) this.closeColorMenu(menu);
    }
  }

  syncSingleForm() {
    if (this.mode !== 'single') return;
    const form = this.formComponent()?.querySelector('form');
    if (!form) return;
    this.formComponent().querySelectorAll('input[name="quantity"]').forEach((input) => { if (input.value !== '1') input.value = '1'; });
    let display = form.querySelector('[data-bogo-display-size]');
    if (!display) { display = document.createElement('input'); display.type = 'hidden'; display.dataset.bogoDisplaySize = ''; form.append(display); }
    const size = this.firstVariant()?.options[this.sizeIndex];
    const label = sizeDisplayLabel(size, this.sizeMap);
    display.name = label.startsWith('US ') ? 'properties[US size]' : 'properties[Size]';
    if (display.value !== label) display.value = label;
  }

  money(cents) {
    if (['GBP', 'CAD', 'AUD'].includes(this.config.currency)) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.config.currency }).format(cents / 100);
    }
    const formatted = formatMoney(cents, this.config.moneyFormat, this.config.currency);
    return new DOMParser().parseFromString(formatted, 'text/html').body.textContent;
  }

  refresh() {
    const first = this.firstVariant();
    const second = this.secondVariant();
    this.querySelector('[data-bogo-total]').textContent = first ? this.money(pairTotal(first.price, second?.price || first.price)) : '';
    this.querySelector('[data-bogo-single-price]').textContent = first ? this.money(first.price) : '';
    const compare = this.querySelector('[data-bogo-single-compare]');
    compare.hidden = !first || !(first.compare_at_price > first.price);
    compare.textContent = first?.compare_at_price ? this.money(first.compare_at_price) : '';
    this.querySelector('[data-bogo-compare]').textContent = first ? this.money(first.price + (second?.price || first.price)) : '';
    this.querySelector('[data-bogo-savings]').textContent = first ? this.money(first.price + (second?.price || first.price) - pairTotal(first.price, second?.price || first.price)) : '';
    this.querySelector('[data-bogo-unit-price]').textContent = first ? this.money(pairTotal(first.price, second?.price || first.price) / 2) : '';
    this.syncControls();
  }

  purchaseButtons() {
    const form = this.formComponent();
    return [...(this.section?.querySelectorAll('[ref="addToCartButton"]') || [])].filter((button) => form?.contains(button) || button.closest(`sticky-add-to-cart[data-product-id="${this.config.productId}"]`));
  }

  syncStickySummary() {
    const sticky = this.section?.querySelector(`sticky-add-to-cart[data-product-id="${this.config.productId}"]`);
    if (!sticky) return;
    const first = this.firstVariant();
    const second = this.secondVariant();
    const pair = this.mode === 'bogo';
    const setText = (selector, text) => {
      const element = sticky.querySelector(selector);
      if (element && element.textContent !== text) element.textContent = text;
    };
    sticky.setAttribute('data-bv-offer-summary', '');
    setText('.sticky-add-to-cart__title', pair ? '2 pairs · 1 FREE' : 'Barevon Original');
    const describe = variant => variant ? `${variant.options[this.colorIndex]} · ${sizeDisplayLabel(variant.options[this.sizeIndex], this.sizeMap)}` : 'Choose your size';
    setText('.sticky-add-to-cart__variant', pair ? `${describe(first)} / ${describe(second)}` : describe(first));
    const price = sticky.querySelector('.sticky-add-to-cart__price');
    if (price) {
      let summary = price.querySelector('[data-bv-sticky-total]');
      if (!summary) { summary = document.createElement('span'); summary.dataset.bvStickyTotal = ''; price.append(summary); }
      const total = first && (!pair || second) ? this.money(pair ? pairTotal(first.price, second.price) : first.price) : '';
      if (summary.textContent !== total) summary.textContent = total;
    }
    const image = sticky.querySelector('.sticky-add-to-cart__image-img');
    const source = first && this.config.colorImages?.[first.options[this.colorIndex]];
    if (image && source && image.getAttribute('src') !== source) {
      image.removeAttribute('srcset');
      image.src = source;
      image.alt = `Barevon Original in ${first.options[this.colorIndex]}`;
    }
  }

  syncControls() {
    if (!this.config || !this.colorSelect) return;
    this.syncNativePickerLock(this.busy || this.singleBusy);
    this.syncSingleForm();
    const available = this.firstVariant()?.available && (this.mode === 'single' || this.secondVariant()?.available);
    const quantityBlocked = this.mode === 'single' && this.formComponent()?.refs?.quantitySelector?.canAddToCart?.()?.canAdd === false;
    const locked = this.busy || this.singleBusy || this.selectionPending || this.uncertain || !this.nativeReady;
    const offerInCart = this.mode === 'bogo' && this.cartHasEligible;
    const blocked = locked || offerInCart || quantityBlocked || !available;
    this.toggleAttribute('data-controls-locked', Boolean(locked));
    this.toggleAttribute('data-purchase-blocked', Boolean(blocked));
    let label = this.mode === 'single' ? this.config.singleButtonLabel : this.config.buttonLabel;
    if (!this.nativeReady) label = 'Preparing your options…';
    else if (this.busy || this.singleBusy) label = this.mode === 'single' ? 'Adding your pair…' : 'Adding both pairs…';
    else if (this.selectionPending) label = 'Updating your first pair…';
    else if (this.uncertain) label = 'Please review your cart';
    else if (offerInCart) label = 'Offer already in your cart';
    else if (quantityBlocked) label = 'Cart quantity limit reached';
    else if (!available) label = this.mode === 'single' ? 'Choose an available pair' : 'Choose two available pairs';
    const shortLabel = label;
    if (!blocked) label += ` · ${this.money(this.mode === 'single' ? this.firstVariant().price : pairTotal(this.firstVariant().price, this.secondVariant().price))}`;
    this.syncStickySummary();
    for (const button of this.purchaseButtons()) {
      if (!this.buttons.has(button)) this.buttons.set(button, { label: button.getAttribute('aria-label') });
      if (!button.hasAttribute('data-bv-studio-offer-button')) button.setAttribute('data-bv-studio-offer-button', '');
      if (button.disabled !== Boolean(blocked)) button.disabled = Boolean(blocked);
      if (button.getAttribute('aria-busy') !== String(this.busy || this.singleBusy)) button.setAttribute('aria-busy', String(this.busy || this.singleBusy));
      if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
      let span = button.querySelector('.bv-studio-offer-cta-label');
      if (!span) { span = document.createElement('span'); span.className = 'bv-studio-offer-cta-label'; button.append(span); }
      const visibleLabel = button.closest('sticky-add-to-cart') ? shortLabel : label;
      if (span.textContent !== visibleLabel) span.textContent = visibleLabel;
    }
    for (const select of [this.firstColorSelect, this.firstSizeSelect, this.colorSelect, this.sizeSelect]) {
      const disabled = Boolean(locked || offerInCart || this.mode === 'single');
      if (select.disabled !== disabled) select.disabled = disabled;
    }
    this.querySelectorAll('[data-bogo-mode]').forEach((radio) => { if (radio.disabled !== Boolean(locked)) radio.disabled = Boolean(locked); });
    this.syncColorMenus();
  }

  captureClick(event) {
    const target = event.target instanceof Element ? event.target.closest('button') : null;
    if (!target || !this.purchaseButtons().includes(target)) return;
    if (this.mode === 'single' && !this.busy && !this.singleBusy && !this.selectionPending && !this.uncertain && this.nativeReady) { this.syncSingleForm(); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addBundle();
  }

  captureSubmit(event) {
    if (event.target !== this.formComponent()?.querySelector('form')) return;
    if (this.mode === 'single' && !this.busy && !this.singleBusy && !this.selectionPending && !this.uncertain && this.nativeReady) { this.syncSingleForm(); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addBundle();
  }

  onProductSelect(event) {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    const generation = ++this.generation;
    if (!this.uncertain && !this.busy && !this.singleBusy) this.clearError();
    this.selectionPending = true;
    this.firstVariantId = '';
    this.syncControls();
    Promise.resolve(event.promise).then(({ detail }) => {
      if (generation !== this.generation) return;
      this.selectionPending = false;
      this.firstVariantId = String(detail?.resource?.id || '');
      const first = this.firstVariant();
      if (first && detail?.resource?.available !== undefined) first.available = detail.resource.available;
      if (first && Number.isFinite(detail?.resource?.price)) first.price = detail.resource.price;
      if (first && Number.isFinite(detail?.resource?.compare_at_price)) first.compare_at_price = detail.resource.compare_at_price;
      this.syncFirstSelectors();
      this.refresh();
    }).catch((error) => {
      if (generation !== this.generation) return;
      this.selectionPending = false;
      this.showError('We could not update your first pair. Please choose its colour and size again.');
      this.syncControls();
    });
  }

  onCartUpdate(event) {
    const nativeSingle = this.mode === 'single' && event.action === 'add' && event.target instanceof Node && this.formComponent()?.contains(event.target);
    if (nativeSingle) { this.singleBusy = true; this.syncControls(); }
    event.promise?.then(({ detail }) => {
      if (Array.isArray(detail?.items)) {
        this.cartHasEligible = eligibleQuantity({ items: detail.items }, this.eligibleProductIds) > 0;
        this.syncControls();
      }
    }).catch(() => {}).finally(() => { if (nativeSingle) { this.singleBusy = false; this.syncControls(); } });
  }

  openFitGuide() {
    const guide = this.section?.querySelector('.bv-product-help__item');
    if (!guide) return;
    guide.open = true;
    guide.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    guide.querySelector('summary')?.focus({ preventScroll: true });
  }

  setStatus(message) {
    this.querySelector('[data-bogo-status]').textContent = message;
  }

  clearError() {
    const error = this.querySelector('[data-bogo-error]');
    error.hidden = true;
    error.textContent = '';
  }

  showError(message, reviewCart = false) {
    const error = this.querySelector('[data-bogo-error]');
    error.textContent = message;
    error.hidden = false;
    this.setStatus('');
    this.querySelector('[data-bogo-cart]').hidden = !reviewCart;
  }

  dispatchCartEvent(event) {
    (this.isConnected ? this : document).dispatchEvent(event);
  }

  endpoint(path) {
    return `${String(this.config.rootUrl || '/').replace(/\/?$/, '/')}${path}`;
  }

  async readCart() {
    const response = await fetch(this.endpoint('cart.js'), { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Your cart could not be checked. Please review it before trying again.');
    return response.json();
  }

  sectionIds() {
    return [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId))].slice(0, 5);
  }

  setCartBusy(busy) {
    if (busy) {
      this.cartLocks = new Map();
      for (const element of document.querySelectorAll('cart-drawer-component, cart-items-component')) {
        this.cartLocks.set(element, { inert: element.inert, busy: element.getAttribute('aria-busy') });
        element.inert = true;
        element.setAttribute('aria-busy', 'true');
      }
    } else {
      for (const [element, previous] of this.cartLocks || []) {
        element.inert = previous.inert;
        if (previous.busy === null) element.removeAttribute('aria-busy'); else element.setAttribute('aria-busy', previous.busy);
      }
      this.cartLocks?.clear();
    }
  }

  async rollback(items) {
    const updates = Object.fromEntries(items.map((item) => [item.key, 0]));
    const response = await fetch(this.endpoint('cart/update.js'), {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, credentials: 'same-origin',
      body: JSON.stringify({ updates, sections: this.sectionIds(), sections_url: window.location.pathname + window.location.search }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error('We could not confirm the cart change. Please review your cart before checkout.');
    if ((data.items || []).some((item) => items.some((previous) => previous.properties?._barevon_bundle === item.properties?._barevon_bundle))) throw new Error('Please review your cart before checkout; the pair offer could not be confirmed.');
    return data;
  }

  resolveCart(deferred, cart, sections, didError) {
    deferred?.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'barevon-studio-offer', sourceId: this.config.productId, itemCount: cart.item_count, productId: String(this.config.productId), sections, didError },
    });
  }

  async addBundle() {
    if (this.mode !== 'bogo' || !this.nativeReady || this.busy || this.singleBusy || this.selectionPending || this.uncertain || this.cartHasEligible) return;
    const first = this.firstVariant();
    const second = this.secondVariant();
    if (!first?.available || !second?.available) { this.showError('Please choose two available pairs.'); return; }
    this.busy = true;
    this.clearError();
    this.setStatus('Checking and adding your two pairs…');
    this.syncControls();
    this.setCartBusy(true);
    let deferred;
    let mutationStarted = false;
    let currentCart;
    try {
      const bundleId = crypto.randomUUID();
      const before = await this.readCart();
      if (eligibleQuantity(before, this.eligibleProductIds) > 0) {
        this.cartHasEligible = true;
        this.showError('Your cart already contains Barevon shoes. This offer allows one free second pair per order. Please review your cart.', true);
        return;
      }
      const labels = Object.fromEntries([first, second].map((variant) => [variant.id, sizeDisplayLabel(variant.options[this.sizeIndex], this.sizeMap)]));
      const items = buildPairItems(first, second, bundleId, labels);
      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchCartEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })), promise: deferred.promise }));
      let responseData = {};
      let addError;
      mutationStarted = true;
      try {
        const response = await fetch(this.endpoint('cart/add.js'), {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, credentials: 'same-origin',
          body: JSON.stringify({ items, sections: this.sectionIds(), sections_url: window.location.pathname + window.location.search }),
        });
        responseData = await response.json();
        if (!response.ok || responseData.status) addError = new Error(responseData.description || responseData.message || 'The selected pairs could not be added.');
      } catch (error) { addError = error; }
      currentCart = await this.readCart();
      const check = inspectBundle(currentCart, bundleId, pairTotal(first.price, second.price), this.config.discountTitle);
      if (!check.valid) {
        if (check.items.length) currentCart = await this.rollback(check.items);
        this.resolveCart(deferred, currentCart, currentCart.sections, true);
        deferred = null;
        const message = addError?.message || 'The second-pair discount could not be applied. Both new pairs were removed so you will not be charged for an incomplete offer. Please refresh or contact us.';
        this.dispatchCartEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        this.showError(message, true);
        return;
      }
      this.resolveCart(deferred, currentCart, responseData.sections, false);
      deferred = null;
      this.cartHasEligible = true;
      this.setStatus('Both pairs are in your cart. Your free pair has been added.');
      this.querySelector('[data-bogo-cart]').hidden = false;
      if (!document.querySelector('cart-drawer-component')) window.location.assign(this.config.cartUrl);
    } catch (error) {
      if (mutationStarted) this.uncertain = true;
      deferred?.reject(error);
      this.dispatchCartEvent(new CartErrorEvent({ error: error.message || 'The cart could not be updated.', code: 'SERVICE_UNAVAILABLE' }));
      this.showError(mutationStarted ? 'We could not confirm the cart. Please review your cart before trying again or checking out.' : (error.message || 'Your cart could not be checked. Please try again.'), mutationStarted);
    } finally {
      this.busy = false;
      this.setCartBusy(false);
      this.syncControls();
    }
  }
}

if (!customElements.get('barevon-studio-offer')) customElements.define('barevon-studio-offer', BarevonStudioOffer);
